<script>
  import { goto } from '@sapper/app';
  import { onMount } from 'svelte';

  import { productDataStore } from '../../stores/ProductData.js';
  import { designState } from '../../stores/Designer.js';
  import { previewLoading, konvaStageId } from '../../stores/Preview.js';
  import { webPSupport } from '../../stores/WebP.js';
  import { designerProductOverride } from '../../stores/DesignerProductOverride.js';

  import ProductOptionCard from '../../components/Design/ProductOptionCard.svelte';
  import ProductTypeSelection from '../../components/Design/ProductTypeSelection.svelte';

  let ext = 'png';
  if ($webPSupport) {
    ext = 'webp';
  }

  export let resizeFunction;

  let selectedType = $productDataStore.showing;

  const typeMap = {
    top: $productDataStore.collectionTops,
    bottom: $productDataStore.collectionBottoms,
    hat: $productDataStore.collectionHats,
    socks: $productDataStore.collectionSocks,
    accessories: $productDataStore.collectionAccessories,
  };

  // If there's no matching type, default to an empty array:
  let productCollectionsSublimation =
    typeMap[selectedType].filter((collection) => collection.Product.isSublimationProduct) || [];

  let productCollectionsNonSublimation =
    typeMap[selectedType].filter((collection) => !collection.Product.isSublimationProduct) || [];

  const typeCollectionMap = {
    top: 'collectionTops',
    bottom: 'collectionBottoms',
    hat: 'collectionHats',
    socks: 'collectionSocks',
    accessories: 'collectionAccessories',
  };

  function changeSelectedType(type) {
    if (!$productDataStore[typeCollectionMap[type]]) return;

    selectedType = type;

    productCollectionsSublimation =
      $productDataStore[typeCollectionMap[type]].filter((collection) => collection.Product.isSublimationProduct) || [];

    productCollectionsNonSublimation =
      $productDataStore[typeCollectionMap[type]].filter((collection) => !collection.Product.isSublimationProduct) || [];

    let gotoUrl;
    // If `top` is set, we always use the top's slug. Otherwise use this type's slug.
    if ($productDataStore.top) {
      gotoUrl = '/design/' + $productDataStore.top.slug + '/choose-uniform-items';
    } else {
      gotoUrl = '/design/' + $productDataStore[$productDataStore.showing].slug + '/choose-uniform-items';
    }

    goto(gotoUrl, { noscroll: true });
  }

  async function changeProduct(productCollection) {
    $previewLoading = true;
    let gender = $productDataStore[$productDataStore.showing].gender;

    const displayColourPath = productCollection.Product.displayColourPath;
    let selectedColour;
    if (displayColourPath) {
      selectedColour = productCollection.Product.Product_Colours.find((x) => x.pathName === displayColourPath);
    } else {
      selectedColour = productCollection.Product.Product_Colours[0];
    }

    let selectedSublimationTemplate;
    if (productCollection.Product.Product_SublimationTemplates) {
      selectedSublimationTemplate =
        productCollection.Product.Product_SublimationTemplates.find((x) => x.isDefault) ||
        productCollection.Product.Product_SublimationTemplates[0];
    }

    let productData = {
      id: productCollection.id,
      name: productCollection.name,
      slug: productCollection.slug,
      rootSlug: productCollection.Product.slug,
      price: productCollection.Product.price,
      reversible: productCollection.Product.reversible,
      mensAvailable: productCollection.Product.mensAvailable,
      womensAvailable: productCollection.Product.womensAvailable,
      youthAvailable: productCollection.Product.youthAvailable,
      editableInDesigner: productCollection.Product.editableInDesigner,
      isModelBasedImages: productCollection.Product.isModelBasedImages,
      useCoverImageForPreview: productCollection.Product.useCoverImageForPreview,
      category: productCollection.ProductCollection.ProductCategory.name,
      collection: productCollection.ProductCollection.name,
      type: productCollection.Product.ProductType.name,
      clipPath: productCollection.Product?.ProductClipPath?.clipPaths,
      printOptions: productCollection.Product.Product_PrintOptions,
      colours: productCollection.Product.Product_Colours,
      sizes: productCollection.Product.Product_Sizes,
      selectedColour: selectedColour,
      deliveryTimes: productCollection.Product.Product_DeliveryTimes,
      gender: gender, // Copy across current gender setting from what was previously showing
      sublimationTemplates: productCollection.Product.Product_SublimationTemplates,
      selectedSublimationTemplate: selectedSublimationTemplate,
      isSublimationProduct: productCollection.Product.isSublimationProduct,
    };

    $productDataStore[productCollection.Product.ProductType.name] = productData;

    // Set showing
    $productDataStore.showing = productCollection.Product.ProductType.name;
    $previewLoading = false;

    // Update the design state with the selected item and the default colour
    designState.addProduct(
      $konvaStageId,
      $productDataStore[productCollection.Product.ProductType.name],
      selectedColour,
      selectedSublimationTemplate,
    );

    // Update URL to match product
    // On mobile devices we also want to advance to the next step
    // We lose the side control panel at widths less than 700px
    let gotoUrl;
    if (window.innerWidth < 700) {
      // Use top URL if it exists
      if ($productDataStore.top) {
        gotoUrl = '/design/' + $productDataStore.top.slug + '/choose-colours';
      } else {
        gotoUrl = '/design/' + $productDataStore[productCollection.Product.ProductType.name].slug + '/choose-colours';
      }
    } else {
      // Use top URL if it exists
      if ($productDataStore.top) {
        gotoUrl = '/design/' + $productDataStore.top.slug + '/choose-uniform-items';
      } else {
        gotoUrl =
          '/design/' + $productDataStore[productCollection.Product.ProductType.name].slug + '/choose-uniform-items';
      }
    }

    await goto(gotoUrl, { noscroll: true });
  }

  function changeCollection() {
    // Goto products collection pages
    // Set product override to true so we don't get the deisgner warning.
    $designerProductOverride = true;
    goto('/products/' + $productDataStore.category.toLowerCase() + '/' + $productDataStore.collection.toLowerCase());
  }

  onMount(() => {
    // Get the #hash from the URL
    let hash = window.location.hash;
    if (hash) {
      changeSelectedType(hash.replace('#', ''));
    }
    resizeFunction();
  });
</script>

<div class="heading">
  <h2>{$productDataStore.collection} Collection</h2>
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div class="change-collection" on:click={changeCollection}>Change</div>
</div>

<div class="content">
  <ProductTypeSelection {selectedType} clickFn={changeSelectedType} />

  <div class="options">
    {#if productCollectionsSublimation.length > 0}
      <div class="product-type-heading">Sublimation Items</div>
      {#each productCollectionsSublimation as productCollection (`sublimation-${productCollection.id}`)}
        <ProductOptionCard
          {productCollection}
          gender={$productDataStore[$productDataStore.showing].gender}
          side={$productDataStore.side}
          selected={$productDataStore[selectedType] && $productDataStore[selectedType].slug === productCollection.slug}
          isModelBasedImages={productCollection.Product.isModelBasedImages}
          useCoverImageForPreview={productCollection.Product.useCoverImageForPreview}
          sublimationTemplateId={productCollection.Product.Product_SublimationTemplates.find((x) => x.isDefault)
            ?.SublimationTemplate?.id}
          clickFn={changeProduct}
        />
      {/each}
    {/if}

    {#if productCollectionsNonSublimation.length > 0}
      <div class="product-type-heading">Classic Stocked Items</div>
      {#each productCollectionsNonSublimation as productCollection (`classic-${productCollection.id}`)}
        <ProductOptionCard
          {productCollection}
          gender={$productDataStore[$productDataStore.showing].gender}
          side={$productDataStore.side}
          selected={$productDataStore[selectedType] && $productDataStore[selectedType].slug === productCollection.slug}
          isModelBasedImages={productCollection.Product.isModelBasedImages}
          useCoverImageForPreview={productCollection.Product.useCoverImageForPreview}
          sublimationTemplateId={productCollection.Product.Product_SublimationTemplates.find((x) => x.isDefault)
            ?.SublimationTemplate?.id}
          clickFn={changeProduct}
        />
      {/each}
    {/if}
  </div>
</div>

<style>
  .heading {
    display: flex;
    align-items: baseline;
    justify-content: center;
    text-transform: capitalize;
    text-align: center;
    font-size: 1.4rem;
    padding-top: 0.5rem;
  }

  .heading h2 {
    margin-left: 2rem;
  }

  .change-collection {
    cursor: pointer;
    font-size: 1.2rem;
    font-weight: 600;
    color: #fe5900;
    margin-left: 2rem;
  }

  .content {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    gap: 8rem;
    padding: 2rem;
    padding-bottom: 60px;
  }

  .options {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 2rem;
    height: fit-content;
  }

  .product-type-heading {
    grid-column: span 2;
    font-size: 1.4rem;
    font-weight: 600;
    color: #fe5900;
    text-transform: uppercase;
    background: #e9e9e9;
    border-radius: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.5rem;
  }

  @media screen and (min-width: 600px) {
    .content {
      grid-template-columns: minmax(0, 80px) minmax(0, 1fr);
      gap: 2rem;
    }

    .options {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    }
  }
</style>
